import { Card, CardContent, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Badge } from "@/shared/ui/badge";
import { MoreVertical, Package, Pencil, Plus, Trash, Building2 } from "lucide-react";
import { Product } from '@/shared/types/productTypes';
import { ServiceLinesSection } from "@/domains/service-lines/components/ServiceLinesSection";
import { useMemo } from "react";
import Dropdown from "@/shared/components/Dropdown";
import { ServiceLine } from "@/shared/types/serviceLineTypes";

interface ProductCardProps {
  product: Product;
  onAddProductToOrganizations?: () => void;
  onManageProduct?: () => void;
  onDeleteProduct?: () => void;
  onRemoveProductFromOrganization?: (orbiusProductReferenceId: string, organizationId: string) => void;
  onManageServiceLine?: (serviceLine: ServiceLine) => void;
}

const ProductCard = ({
  product,
  onAddProductToOrganizations,
  onManageProduct,
  onDeleteProduct,
  onRemoveProductFromOrganization,
  onManageServiceLine,
}: ProductCardProps) => {
  const dropdownMenuItems = useMemo(() => {
    const items = [];
    if (onAddProductToOrganizations && onManageProduct) {
      items.push({
        label: 'Add to Organizations',
        icon: Plus,
        variant: 'default',
        onClick: onAddProductToOrganizations,
      },
        {
          label: 'Manage',
          icon: Pencil,
          variant: 'default',
          onClick: onManageProduct,
        });
    }
    if (onDeleteProduct && (product.organizations?.length ?? 0) === 0) {
      items.push({
        label: 'Delete',
        icon: Trash,
        variant: 'destructive',
        onClick: onDeleteProduct,
      });
    }
    return items;
  }, [product, onAddProductToOrganizations, onManageProduct, onDeleteProduct]);

  return (
    <Card className="flex flex-col gap-4">
      <CardContent className="flex flex-col gap-4 p-4">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-background rounded-lg flex items-center justify-center border">
              <Package className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <CardTitle className="text-lg">{product.orbiusProductName}</CardTitle>
              </div>
              <div className="flex flex-col items-start space-y-1">
                <div className="flex flex-row items-center gap-2">
                  {product?.dataProduct?.dataAmount !== null && (
                    <Badge variant="default">Base data amount: {product.dataProduct.dataAmount} {product.dataProduct.dataUnit}</Badge>
                  )}
                  {product?.l2vpnSupported === true && (
                    <Badge variant="default">L2VPN Supported</Badge>
                  )}
                </div>
                {product.starlinkProductName && (
                  <p className="text-sm font-medium text-muted-foreground">{product.starlinkProductName}</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          {dropdownMenuItems.length > 0 && (
            <Dropdown triggerIcon={MoreVertical} items={dropdownMenuItems} />
          )}
        </div>

        {/* Accounts Section */}
        {product.organizations && product.organizations.length > 0 ? (
          <div className="space-y-2">
            <span className="text-sm font-medium">Associated Account{product.organizations.length > 1 ? 's' : ''} ({product.organizations.length})</span>
            <div className="space-y-2">
              {product.organizations.map((organization) => (
                <div key={organization.organizationId} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <div>
                        <p className="text-sm font-medium">{organization.name}</p>
                      </div>
                    </div>
                    {onRemoveProductFromOrganization && (organization.hasServiceLine ?? false) === false && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveProductFromOrganization?.(product.orbiusProductReferenceId, organization.organizationId);
                        }}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {product.serviceLines?.length > 0 ? (
              <ServiceLinesSection serviceLines={product.serviceLines} onManageServiceLine={onManageServiceLine} />
            ) : (
              <p className="text-sm italic text-red-500">No service lines associated with this product yet.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductCard; 